import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Observable} from 'rxjs';
import {map, startWith, switchMap} from 'rxjs/operators';
import {FormControl} from '@angular/forms';
import {MatPaginator} from '@angular/material/paginator';
import {SubSink} from 'subsink';
import {MatTableDataSource} from '@angular/material/table';


export interface BuyerIdName {
  id: number;
  name: string;
}

export interface BuyerChange {
  type: 'REMOVE' | 'ADD',
  payload: BuyerIdName
}

@Component({
  selector: 'app-buyers-edit',
  template: `
    <form>
      <div class="form-input-container">
        <mat-form-field>
          <input class="buyerSearch" placeholder="Add"
                 aria-label="Add buyer"
                 [formControl]="buyerControl"
                 matInput
                 [matAutocomplete]="buyers">
          <mat-autocomplete #buyers="matAutocomplete"
                            (optionSelected)="onBuyerSelected($event.option.value)">
            <mat-option *ngFor="let buyer of filteredBuyers$ | async" [value]="buyer">
              {{buyer.name}} ({{buyer.id}})
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>
      </div>
      <table mat-table [dataSource]="matDataSource" class="mat-elevation-z8">
        <!-- Id Column-->
        <ng-container matColumnDef="id">
          <th mat-header-cell *matHeaderCellDef> Id</th>
          <td mat-cell *matCellDef="let element"> {{element.id}} </td>
        </ng-container>
        <!-- Name Column -->
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef> Name</th>
          <td mat-cell *matCellDef="let element"> {{element.name}} </td>
        </ng-container>
        <!-- Actions Column-->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef> Actions</th>
          <td mat-cell *matCellDef="let element">
            <button mat-stroked-button color="warn" (click)="remove(element)">Remove</button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
      </table>
      <mat-paginator [pageSizeOptions]="[3, 10, 50]"
                     showFirstLastButtons
                     aria-label="Select page">
      </mat-paginator>
    </form>
  `,
  styleUrls: ['./buyers-edit.component.scss']
})
export class BuyersEditComponent<T extends BuyerIdName> implements OnInit, OnDestroy, AfterViewInit {

  @Input() buyersSource!: Observable<T[]>;
  @Input() selectedBuyers!: Observable<T[]>;
  @Output() buyerChange: EventEmitter<BuyerChange> = new EventEmitter<BuyerChange>();
  @ViewChild(MatPaginator) matPaginator!: MatPaginator;
  matDataSource = new MatTableDataSource();
  filteredBuyers$!: Observable<BuyerIdName[]>;
  displayedColumns = ['id', 'name', 'actions'];
  buyerControl = new FormControl();

  private subsink = new SubSink();

  constructor() {
  }

  ngAfterViewInit(): void {
    this.matDataSource.paginator = this.matPaginator;
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  remove(element: BuyerIdName) {
    this.buyerChange.emit({
      type: 'REMOVE',
      payload: element
    });
  }

  onBuyerSelected(element: BuyerIdName) {
    this.buyerControl.setValue('');
    this.buyerChange.emit({
      type: 'ADD',
      payload: element
    });
  }

  ngOnInit(): void {
    this.subsink.sink = this.selectedBuyers.subscribe(
      buyers => this.matDataSource.data = buyers
    );
    this.filteredBuyers$ = this.buyerControl.valueChanges.pipe(
      startWith(''),
      switchMap(filterValue => this.buyersSource
        .pipe(
          map(buyer => buyer.filter(value =>
            value.name.toLowerCase().includes(String(filterValue)?.toLowerCase())
            || value.id.toString().includes(String(filterValue)?.toLowerCase()))
          ),
          map(buyer => buyer.slice(0, 100))
        )
      )
    );
  }
}
